import React, { useRef, useState } from 'react';
import CanvasFrameContent from '../canvas/CanvasFrameContent';
import '../canvas/canvasStage.css';
import { useCanvasEditorViewport } from './CanvasEditorViewportContext';
import type { CanvasFrameFieldPatch } from './canvasEditorTypes';
import { resizeFrame, rotateFromPointer, screenDeltaToLocalDelta, type ResizeHandle } from './frameGeometry';
import type { CanvasFrame } from '../types';

interface Props {
  frame: CanvasFrame;
  selected: boolean;
  onSelect: () => void;
  /** Vuurt pas op pointerup met de uiteindelijke verandering -- niet per
   * pixel tijdens het slepen zelf (dat blijft lokale, optimistic UI). */
  onCommit: (patch: CanvasFrameFieldPatch) => void;
}

const HANDLES: ResizeHandle[] = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'];
const DRAG_THRESHOLD = 4;

type DragState =
  | { kind: 'move'; startX: number; startY: number; startBoxX: number; startBoxY: number; moved: boolean }
  | { kind: 'resize'; handle: ResizeHandle; startX: number; startY: number; startBox: { x: number; y: number; width: number; height: number; rotation: number } }
  | { kind: 'rotate'; centerX: number; centerY: number; startAngle: number; startRotation: number };

/**
 * Interactieve editor-versie van een frame: dezelfde visuele inhoud als
 * CanvasFrameElement (via het gedeelde CanvasFrameContent), plus selectie,
 * 8 resize-handles en 1 rotatiehandle via kale pointer-events. Lokale
 * optimistic UI tijdens het slepen (draft-state); commit (met revision, via
 * de aanroepende pagina) pas op pointerup. Een klik zonder beweging
 * selecteert alleen -- geen loze API-call.
 */
export default function CanvasFrameEditable({ frame, selected, onSelect, onCommit }: Props) {
  const { scale: zoomScale } = useCanvasEditorViewport();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const [draft, setDraft] = useState<CanvasFrameFieldPatch | null>(null);

  const box = {
    x: draft?.x ?? frame.x, y: draft?.y ?? frame.y,
    width: draft?.width ?? frame.width, height: draft?.height ?? frame.height,
  };
  const rotation = draft?.rotation ?? frame.rotation;

  const handleBodyPointerDown = (event: React.PointerEvent) => {
    event.stopPropagation();
    onSelect();
    dragRef.current = { kind: 'move', startX: event.clientX, startY: event.clientY, startBoxX: frame.x, startBoxY: frame.y, moved: false };
  };

  const handleResizePointerDown = (handle: ResizeHandle) => (event: React.PointerEvent) => {
    event.stopPropagation();
    try { wrapperRef.current?.setPointerCapture(event.pointerId); } catch { /* niet elke input-context ondersteunt dit */ }
    dragRef.current = {
      kind: 'resize', handle, startX: event.clientX, startY: event.clientY,
      startBox: { x: frame.x, y: frame.y, width: frame.width, height: frame.height, rotation: frame.rotation },
    };
  };

  const handleRotatePointerDown = (event: React.PointerEvent) => {
    event.stopPropagation();
    try { wrapperRef.current?.setPointerCapture(event.pointerId); } catch { /* niet elke input-context ondersteunt dit */ }
    const rect = wrapperRef.current?.getBoundingClientRect();
    const centerX = rect ? rect.left + rect.width / 2 : event.clientX;
    const centerY = rect ? rect.top + rect.height / 2 : event.clientY;
    dragRef.current = {
      kind: 'rotate', centerX, centerY,
      startAngle: Math.atan2(event.clientY - centerY, event.clientX - centerX),
      startRotation: frame.rotation,
    };
  };

  const handlePointerMove = (event: React.PointerEvent) => {
    const drag = dragRef.current;
    if (!drag) return;

    if (drag.kind === 'move') {
      const screenDx = event.clientX - drag.startX;
      const screenDy = event.clientY - drag.startY;
      if (!drag.moved && Math.hypot(screenDx, screenDy) < DRAG_THRESHOLD) return;
      if (!drag.moved) {
        try { wrapperRef.current?.setPointerCapture(event.pointerId); } catch { /* niet elke input-context ondersteunt dit */ }
      }
      drag.moved = true;
      setDraft({ x: drag.startBoxX + screenDx / zoomScale, y: drag.startBoxY + screenDy / zoomScale });
      return;
    }

    if (drag.kind === 'resize') {
      const screenDx = event.clientX - drag.startX;
      const screenDy = event.clientY - drag.startY;
      const { dx, dy } = screenDeltaToLocalDelta(screenDx, screenDy, drag.startBox.rotation, zoomScale);
      setDraft(resizeFrame(drag.startBox, drag.handle, dx, dy));
      return;
    }

    const pointerAngle = Math.atan2(event.clientY - drag.centerY, event.clientX - drag.centerX);
    setDraft({ rotation: rotateFromPointer(pointerAngle, drag.startAngle, drag.startRotation) });
  };

  const endInteraction = () => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (!drag) return;
    if (drag.kind === 'move' && !drag.moved) return; // zuivere klik: alleen selecteren (al gebeurd bij pointerdown)
    if (draft) onCommit(draft);
    setDraft(null);
  };

  return (
    <div
      ref={wrapperRef}
      className={`canvas-frame canvas-frame--${frame.shape} canvas-frame--level-${frame.level} canvas-frame-editable${selected ? ' canvas-frame-editable--selected' : ''}`}
      style={{ left: box.x, top: box.y, width: box.width, height: box.height, transform: rotation ? `rotate(${rotation}deg)` : undefined }}
      onPointerDown={handleBodyPointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endInteraction}
      onPointerCancel={endInteraction}
    >
      <CanvasFrameContent frame={frame} />
      {selected && HANDLES.map((handle) => (
        <div
          key={handle}
          className={`canvas-frame-editable__handle canvas-frame-editable__handle--${handle}`}
          onPointerDown={handleResizePointerDown(handle)}
        />
      ))}
      {selected && <div className="canvas-frame-editable__rotate-handle" onPointerDown={handleRotatePointerDown} />}
    </div>
  );
}
